import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SearchClientsResult } from "@/lib/coach-manager/client";
import type { Client } from "@/lib/coach-manager/types";
import { clientSearchKeys } from "@/lib/query";
import type { ClientSearchRequest } from "@/schemas/clients";

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

/**
 * The current user's persisted last Client Search — one row per user (see
 * `client_search_state`), superseded on every new search attempt regardless
 * of outcome.
 */
export interface ClientSearchState {
  criteria: ClientSearchRequest;
  status: "success" | "error";
  clients: Client[] | null;
  errorMessage: string | null;
  searchedAt: string;
}

/**
 * Search Coach Manager for Clients via a saved BU instance. Mirrors
 * `useSearchContracts`'s shape exactly — a `useMutation` with no cache
 * invalidation (read-only probe). Never throws on a Coach-Manager-level
 * failure (`{ ok: false, error }` is a valid result the caller renders
 * inline); only throws on a transport/validation failure of the route itself
 * (401/400/404). A successful search also caches the returned clients
 * server-side (see `src/api/client-search.ts`) — the Copy Contract Target
 * Client picker (`useCachedClients`) picks up newly-cached clients the next
 * time it queries, no explicit cross-page invalidation needed.
 */
export function useSearchClients() {
  return useMutation({
    mutationFn: async (input: ClientSearchRequest): Promise<SearchClientsResult> => {
      const res = await fetch("/api/coach-manager/clients/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Client search failed"));
      return res.json();
    },
  });
}

/** The persisted last Client Search for the current user, or `null` if none. */
export function useLastClientSearch() {
  return useQuery({
    queryKey: clientSearchKeys.last,
    queryFn: async (): Promise<ClientSearchState | null> => {
      const res = await fetch("/api/coach-manager/clients/search/last");
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to load last search"));
      const json = await res.json();
      return json.state;
    },
  });
}

/** Clears the persisted last Client Search for the current user. */
export function useClearLastClientSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/coach-manager/clients/search/last", { method: "DELETE" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to clear search"));
    },
    onSuccess: () => {
      queryClient.setQueryData(clientSearchKeys.last, null);
    },
  });
}
